import React, { useState, useEffect, useRef } from 'react'
// import Client from 'shopify-buy/index.unoptimized.umd.min'
// import {createStorefrontApiClient} from '@shopify/storefront-api-client';
import client from '~/utils/client'

import Context from '~/context/StoreContext'

// const client = Client.buildClient({
//   storefrontAccessToken: process.env.SHOPIFY_ACCESS_TOKEN,
//   domain: process.env.SHOP_NAME,
//   apiVersion: process.env.SHOPIFY_API_VERSION ?? '2022-10'
// })

// const client = createStorefrontApiClient({
//   storeDomain: process.env.SHOP_NAME,
//   apiVersion: process.env.SHOPIFY_STOREFRONT_API_VERSION ?? '2025-01',
//   publicAccessToken: process.env.SHOPIFY_ACCESS_TOKEN,
// });

const cartFields = `
id
attributes {
  key
  value
}
checkoutUrl
createdAt
updatedAt
lines (first: 250) {
  nodes {
    id
    quantity
    merchandise {
      ...on ProductVariant {
        id
        product {
          id
          title
          handle
        }
        title
        price {
          amount
        }
        image {
          src
        }
        selectedOptions {
          name
          value
        }
      }
    }
  }
}
totalQuantity
cost {
  subtotalAmount {
    amount
    currencyCode
  }
  totalAmount {
    amount
  }
}`

const cartCreateMutation = `
mutation cartCreate($input: CartInput) {
  cartCreate(input: $input) {
    cart {
      ${cartFields}
    }
    userErrors {
      field
      message
    }
    warnings {
      code
      message
      target
    }
  }
}`

const cartQuery = `
query getCart($id: ID!) {
  cart(id: $id) {
    ${cartFields}
  }
}`

const cartLinesAddMutation = `
mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
  cartLinesAdd(cartId: $cartId, lines: $lines) {
    cart {
      ${cartFields}
    }
    userErrors {
      field
      message
    }
    warnings {
      code
      message
      target
    }
  }
}`

const cartLinesRemoveMutation = `
mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
  cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
    cart {
      ${cartFields}
    }
    userErrors {
      field
      message
    }
    warnings {
      code
      message
      target
    }
  }
}`

const cartLinesUpdateMutation = `
mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
  cartLinesUpdate(cartId: $cartId, lines: $lines) {
    cart {
        ${cartFields}
    }
    userErrors {
      field
      message
    }
    warnings {
      code
      message
      target
    }
  }
}`

/**
 * After migration from Checkout Storefront API to Cart Storefront API, the response structure has changed. The old
 * format is still widely used in the codebase, so we need to update the response structure to match the old one.
 */
function normalizeCart(cart) {
  cart.lines = cart.lines.nodes
  // merchandise to variant
  cart.lines = cart.lines.map(line => {
    line.variant = line.merchandise
    return line
  })
  cart.webUrl = cart.checkoutUrl
  return cart
}

const ContextProvider = ({ children }) => {
  let initialStoreState = {
    client,
    adding: false,
    addingV2: {},
    checkout: { lineItems: [], lines: [], totalQuantity: 0 },
    products: [],
    shop: {},
  }

  const [store, updateStore] = useState(initialStoreState)
  const isRemoved = useRef(false)

  useEffect(() => {
    const initializeCheckout = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== 'undefined'
      const existingCheckoutID = isBrowser
        ? localStorage.getItem('shopify_checkout_id')
        : null

      const setCheckoutInState = checkout => {
        if (isBrowser) {
          localStorage.setItem('shopify_checkout_id', checkout.id)
        }

        updateStore(prevState => {
          return { ...prevState, checkout }
        })
      }

      // const createNewCheckout = () => store.client.checkout.create({
      //   customAttributes: [
      //     {
      //       key: 'Salon',
      //       value: "salonData" in window ? window.salonData.name : window.location.host
      //     }
      //   ]
      // });
      const createNewCheckout = () => client.request(cartCreateMutation, {
        variables: {
            input: {
                attributes: [
                    {
                        key: 'Salon',
                        value: "salonData" in window ? window.salonData.name : window.location.host
                    }
                ]
            }
        }
      }).then(res => res.data.cartCreate.cart)
      // const fetchCheckout = id => store.client.checkout.fetch(id)
      const fetchCheckout = id => client.request(cartQuery, {
        variables: {
          id: id
        }
      }).then(res => res.data.cart)
          .then(normalizeCart)

      if (existingCheckoutID) {
        try {
          const checkout = await fetchCheckout(existingCheckoutID)
          // Make sure this cart hasn't already been purchased
          if (checkout && !isRemoved.current) {
            setCheckoutInState(checkout)
            return
          }
        } catch (e) {
          localStorage.setItem('shopify_checkout_id', null)
        }
      }

      const newCheckout = await createNewCheckout()
      if (!isRemoved.current) {
        setCheckoutInState(newCheckout)
      }
    }

    initializeCheckout()
  }, [isRemoved, store.client])

  useEffect(() => () => {
    isRemoved.current = true
  })

  return (
    <Context.Provider
      value={{
        store,
        addVariantToCart: (merchandiseId, quantity) => {
          if (merchandiseId === '' || !quantity) {
            console.error('Both a size and quantity are required.')
            return
          }

          updateStore(prevState => {
            let addingV2 = prevState.addingV2;
            addingV2[merchandiseId] = true;
            return {
              ...prevState,
              adding: true,
              addingV2: addingV2
            }
          })

          const { checkout, client } = store

          const checkoutId = checkout.id
          const lines = [
            { merchandiseId, quantity: parseInt(quantity, 10) },
          ]

          // return client.checkout
          //   .addLineItems(checkoutId, lines)
          //   .then(checkout => {
          //     updateStore(prevState => {
          //       let addingV2 = prevState.addingV2;
          //       addingV2[merchandiseId] = false;
          //       return {
          //         ...prevState,
          //         checkout,
          //         adding: false,
          //         addingV2: addingV2
          //       }
          //     })
          //   })

          return client.request(cartLinesAddMutation, {
            variables: {
              cartId: checkoutId,
              lines
            }
          }).then(res => res.data.cartLinesAdd.cart)
            .then(normalizeCart)
            .then(cart => {
              updateStore(prevState => {
                let addingV2 = prevState.addingV2;
                addingV2[merchandiseId] = false;
                return {
                  ...prevState,
                  checkout: cart,
                  adding: false,
                  addingV2: addingV2
                }
              })
            })
        },
        removeLineItem: (client, checkoutID, lineItemID) => {
          // return client.checkout
          //   .removeLineItems(checkoutID, [lineItemID])
          //   .then(res => {
          //     updateStore(prevState => {
          //       return { ...prevState, checkout: res }
          //     })
          //   })

          return client.request(cartLinesRemoveMutation, {
            variables: {
              cartId: checkoutID,
              lineIds: [lineItemID]
            }
          }).then(res => res.data.cartLinesRemove.cart)
            .then(normalizeCart)
            .then(cart => {
              updateStore(prevState => {
                return {...prevState, checkout: cart}
              })
            })
        },
        updateLineItem: (client, checkoutID, lineItemID, quantity) => {
          const lines = [
            { id: lineItemID, quantity: parseInt(quantity, 10) },
          ]

          // return client.checkout
          //   .updateLineItems(checkoutID, lineItemsToUpdate)
          //   .then(res => {
          //     updateStore(prevState => {
          //       return { ...prevState, checkout: res }
          //     })
          //   })

          return client.request(cartLinesUpdateMutation, {
            variables: {
              cartId: checkoutID,
              lines
            }
          }).then(res => res.data.cartLinesUpdate.cart)
            .then(normalizeCart)
            .then(cart => {
              updateStore(prevState => {
                return {...prevState, checkout: cart}
              })
            })
        },
        updateSalonName: () => {
          isRemoved.current = true;
        }
      }}
    >
      {children}
    </Context.Provider>
  )
}
export default ContextProvider
